import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, CheckCircle, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <HeroSection />

      {/* Contact Form & Info */}
      <ContactSection />
    </div>
  );
};

const HeroSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#004C97] to-[#003366] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Get in Touch
          </h1>
          <p className="text-xl md:text-2xl text-[#D4EDF7] max-w-3xl mx-auto leading-relaxed">
            Ready to transform your insurance operations? Let's start a conversation
          </p>
        </motion.div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-[#004C97] mb-6">
              Send us a Message
            </h2>
            <p className="text-gray-600 mb-8">
              Fill out the form below and we'll get back to you as soon as possible
            </p>
            <ContactForm />
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="lg:pl-8"
          >
            <h2 className="text-4xl font-bold text-[#004C97] mb-6">
              Contact Information
            </h2>
            <p className="text-gray-600 mb-12">
              We're here to help. Reach out to us through any of these channels
            </p>

            <div className="space-y-8">
              <ContactInfoCard
                icon={MapPin}
                title="Our Location"
                content="Kuala Lumpur, Malaysia"
                delay={0}
              />
              <ContactInfoCard
                icon={Mail}
                title="Email Us"
                content="aseem@randovatelabs.com"
                delay={0.1}
              />
              <ContactInfoCard
                icon={Phone}
                title="Call Us"
                content="+60 11 2170 0240"
                delay={0.2}
              />
            </div>

            {/* Decorative Element */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 1, delay: 0.5 }}
              className="mt-12 p-8 bg-gradient-to-br from-[#D4EDF7] to-white rounded-2xl"
            >
              <h3 className="text-2xl font-bold text-[#004C97] mb-4">
                Why Work With Us?
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#004C97] flex-shrink-0 mt-1" size={20} />
                  <span>100% focus on insurance domain</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#004C97] flex-shrink-0 mt-1" size={20} />
                  <span>Proven track record with industry leaders</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#004C97] flex-shrink-0 mt-1" size={20} />
                  <span>Agile and responsive approach</span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ContactInfoCard = ({ icon: Icon, title, content, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-[#D4EDF7] hover:border-[#004C97] transition-colors"
    >
      <div className="w-12 h-12 bg-[#D4EDF7] rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="text-[#004C97]" size={24} />
      </div>
      <div>
        <h4 className="font-semibold text-[#004C97] mb-1">{title}</h4>
        <p className="text-gray-600">{content}</p>
      </div>
    </motion.div>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSuccess(true);
        toast.success(response.data.message);
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
        setTimeout(() => setIsSuccess(false), 3000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
          Full Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#004C97] focus:outline-none transition-colors"
          placeholder="John Doe"
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
          Email Address *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#004C97] focus:outline-none transition-colors"
          placeholder="john@example.com"
        />
      </div>

      <div>
        <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
          Company Name
        </label>
        <input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#004C97] focus:outline-none transition-colors"
          placeholder="Your Company"
        />
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
          Phone Number
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#004C97] focus:outline-none transition-colors"
          placeholder="+60 123 456 789"
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
          Message *
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows="5"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#004C97] focus:outline-none transition-colors resize-none"
          placeholder="Tell us about your project..."
        />
      </div>

      <motion.button
        type="submit"
        disabled={isSubmitting || isSuccess}
        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
        className={`w-full py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-3 transition-all shadow-lg ${
          isSuccess
            ? 'bg-green-500 text-white'
            : 'bg-[#004C97] text-white hover:bg-[#003366]'
        } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
      >
        {isSuccess ? (
          <>
            <CheckCircle size={24} />
            Message Sent!
          </>
        ) : isSubmitting ? (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
            />
            Sending...
          </>
        ) : (
          <>
            Send Message
            <Send size={20} />
          </>
        )}
      </motion.button>
    </form>
  );
};

export default Contact;
